import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Employee {
  id: string
  name: string
  department: string
  role: string
  performance_score: number
  tenure_months: number
  status: string
}

interface Metric {
  id: string
  name: string
  category: string
  value: number
  trend: string
  change_percent: number
}

interface Statistics {
  total_employees: number
  active_employees: number
  avg_performance: number
  turnover_rate: number
  open_positions: number
  avg_tenure_months: number
  departments: number
  training_completion: number
}

export default function WorkforceIntelligencePage() {
  const [employees, setEmployees] = useState<Employee[]>([])
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'employees'>('overview')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [metricsRes, employeesRes, statsRes] = await Promise.all([
        fetch('/api/workforceintelligence/metrics'),
        fetch('/api/workforceintelligence/talent'),
        fetch('/api/workforceintelligence/statistics')
      ])

      if (metricsRes.ok) setMetrics(await metricsRes.json())
      if (employeesRes.ok) setEmployees(await employeesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching workforce intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      on_leave: 'bg-yellow-100 text-yellow-800',
      terminated: 'bg-red-100 text-red-800',
      pending: 'bg-gray-100 text-gray-800'
    }
    return classes[status.toLowerCase().replace(' ', '_')] || 'bg-gray-100 text-gray-800'
  }

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return '↑'
    if (trend === 'down') return '↓'
    return '→'
  }

  const getTrendClass = (trend: string) => {
    if (trend === 'up') return 'text-green-600'
    if (trend === 'down') return 'text-red-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Workforce Intelligence</h1>
          <p className="mt-2 text-gray-600">AI-powered workforce analytics and talent insights</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_employees}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Performance</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_performance.toFixed(1)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Turnover Rate</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.turnover_rate.toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Positions</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.open_positions}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Metrics Overview
            </button>
            <button
              onClick={() => setActiveTab('employees')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'employees'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Talent Pool ({employees.length})
            </button>
          </nav>
        </div>

        {/* Metrics Grid */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {metrics.map((metric) => (
              <div key={metric.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">{metric.category}</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{metric.name}</p>
                  </div>
                  <span className={`text-xl font-bold ${getTrendClass(metric.trend)}`}>
                    {getTrendIcon(metric.trend)}
                  </span>
                </div>
                <div className="mt-4">
                  <p className="text-3xl font-bold text-gray-900">{metric.value.toLocaleString()}</p>
                  <p className={`text-sm mt-1 ${getTrendClass(metric.trend)}`}>
                    {metric.change_percent > 0 ? '+' : ''}{metric.change_percent.toFixed(1)}% from last period
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Employees Table */}
        {activeTab === 'employees' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenure</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.slice(0, 20).map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{employee.name}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.department}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${employee.performance_score >= 80 ? 'bg-green-500' : employee.performance_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${employee.performance_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{employee.performance_score}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {Math.floor(employee.tenure_months / 12)}y {employee.tenure_months % 12}m
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(employee.status)}`}>
                        {employee.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
